import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import {
  askDeleteFn,
  ButtonComponent,
  ChatBubbleComponent,
  IconComponent,
  LoadingSpinnerComponent,
  messageFn,
  MultilineInputComponent,
  SoftSlotDirective,
  translator,
} from '@softline/ui-core';
import { UserInfoService } from '@softline/auth';
import {
  MasterdataDocumentNoteCategory,
  MasterdataInputComponent,
} from '@softapps/core/masterdata';
import { ensureDocumentsTranslations } from '../documents.i18n';
import { DocumentNote } from '../types/document-note';
import { DocumentNoteRepository } from './document-note.repository';
import { DocumentNoteService } from './document-note.service';

/** The slice of a document the notes panel needs for its "created by" activity line. */
export interface DocumentNotesDocument {
  user?: { name?: string | null; key?: string | null } | null;
  documentType?: { displayName?: string | null } | null;
  documentDate?: string | null;
}

@Component({
  selector: 'soft-document-notes',
  templateUrl: './document-notes.component.html',
  styleUrls: ['./document-notes.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: DocumentNoteRepository, useClass: DocumentNoteRepository },
    DocumentNoteService,
  ],
  imports: [
    ButtonComponent,
    ChatBubbleComponent,
    IconComponent,
    LoadingSpinnerComponent,
    MasterdataInputComponent,
    MultilineInputComponent,
    SoftSlotDirective,
    DatePipe,
  ],
})
export class DocumentNotesComponent {
  private readonly t = translator();
  private readonly noteService = inject(DocumentNoteService);
  private readonly translationsReady = ensureDocumentsTranslations();
  private readonly askDelete = askDeleteFn();
  private readonly message = messageFn();
  private readonly userInfo = inject(UserInfoService);

  /** Id of the document whose notes are shown. `null` while a draft is being
   *  created and not yet persisted — notes can't exist without a document. */
  readonly documentId = input<string | null>(null);
  readonly readonly = input(false);
  /** Own title bar. Off where the host already provides one (e.g. a page panel header). */
  readonly showHeader = input(true);
  /** Source of the "created by" activity line; `null` hides it. */
  readonly document = input<DocumentNotesDocument | null>(null);
  /** The live note list, emitted once loaded and after every change — consumers keep
   *  their document's notes snapshot (tab badge) in sync from it. */
  readonly notesChange = output<DocumentNote[]>();

  readonly notes = this.noteService.notes;
  readonly isLoading = this.noteService.isLoading;
  readonly isSaving = this.noteService.isSaving;
  readonly loadFailed = this.noteService.loadFailed;

  /** Activity line above the first message: who created the document, when. */
  readonly createdActivity = computed(() => {
    const document = this.document();
    if (!document) return null;
    const user = document.user?.name?.trim() || document.user?.key?.trim();
    const type = document.documentType?.displayName?.trim();
    if (!user || !type) return null;
    return { user, type, date: document.documentDate ?? null };
  });

  /** Chat order: oldest first, newest at the bottom. */
  readonly sortedNotes = computed(() =>
    [...this.notes()].sort(
      (a, b) =>
        (a.creationDate ?? '').localeCompare(b.creationDate ?? '') ||
        (a.lastChanged ?? '').localeCompare(b.lastChanged ?? '')
    )
  );

  /** Id of the note currently being edited inline, or null. */
  readonly editingId = signal<string | null>(null);
  readonly editText = signal('');
  readonly editCategory = signal<MasterdataDocumentNoteCategory | null>(null);
  readonly editCategoryKey = computed(() => this.editCategory()?.key ?? null);

  /** Composer state — always visible at the bottom while editable. */
  readonly newText = signal('');
  readonly newCategory = signal<MasterdataDocumentNoteCategory | null>(null);
  readonly newCategoryKey = computed(() => this.newCategory()?.key ?? null);

  readonly canEdit = computed(() => !this.readonly() && !!this.documentId());

  private readonly messagesEl = viewChild<ElementRef<HTMLElement>>('messages');

  constructor() {
    effect(() => {
      if (!this.noteService.hasLoaded()) return;
      this.notesChange.emit(this.noteService.notes());
    });
    effect(() => {
      const id = this.documentId();
      if (id) void this.noteService.load(id);
      else this.noteService.reset();
    });
    effect(() => {
      if (this.sortedNotes().length === 0) return;
      const el = this.messagesEl()?.nativeElement;
      if (el) setTimeout(() => el.scrollTo({ top: el.scrollHeight }));
    });
  }

  reload(): void {
    const id = this.documentId();
    if (id) void this.noteService.load(id);
  }

  isOwn(note: DocumentNote): boolean {
    const login = this.userInfo.value()?.userName?.trim().toLowerCase();
    const author = note.user?.key?.trim().toLowerCase();
    return !!login && !!author && login === author;
  }

  /** Display name for the note's author. The API often returns `user.name` as
   *  null and only fills `user.key` (the login), so fall back to it. */
  userLabel(note: DocumentNote): string {
    return note.user?.name?.trim() || note.user?.key?.trim() || '—';
  }

  async saveAdd(): Promise<void> {
    const id = this.documentId();
    const text = this.newText().trim();
    const categoryKey = this.newCategoryKey();
    if (!id || !text || !categoryKey || this.isSaving()) return;
    const created = await this.noteService.create(id, {
      text,
      categoryKey,
    });
    if (created) {
      this.message.success(this.t('#WWS_DOCUMENTS.NOTES.MESSAGES.CREATED'));
      // keep the category for the next message, only clear the text
      this.newText.set('');
    }
  }

  startEdit(note: DocumentNote): void {
    this.editingId.set(note.id);
    this.editText.set(note.text);
    // The note only stores {key, name}; seed a display-compatible record —
    // the dropdown lists it until the user picks a real masterdata row.
    this.editCategory.set(
      note.category?.key
        ? ({ ...note.category, id: note.category.key } as MasterdataDocumentNoteCategory)
        : null
    );
  }

  cancelEdit(): void {
    this.editingId.set(null);
    this.editText.set('');
    this.editCategory.set(null);
  }

  async saveEdit(): Promise<void> {
    const id = this.documentId();
    const noteId = this.editingId();
    const text = this.editText().trim();
    const categoryKey = this.editCategoryKey();
    if (!id || !noteId || !text || !categoryKey) return;
    const updated = await this.noteService.update(id, noteId, {
      text,
      categoryKey,
    });
    if (updated) {
      this.message.success(this.t('#WWS_DOCUMENTS.NOTES.MESSAGES.UPDATED'));
      this.cancelEdit();
    }
  }

  async deleteNote(note: DocumentNote): Promise<void> {
    const id = this.documentId();
    if (!id) return;
    const result = await this.askDelete({
      title: this.t('#WWS_DOCUMENTS.NOTES.DELETE.TITLE'),
      question: this.t('#WWS_DOCUMENTS.NOTES.DELETE.QUESTION'),
    });
    if (result !== 'YES') return;
    const ok = await this.noteService.delete(id, note.id);
    if (ok) this.message.success(this.t('#WWS_DOCUMENTS.NOTES.MESSAGES.DELETED'));
  }

  onEditComposerEnter(event: Event): void {
    const keyboardEvent = event as KeyboardEvent;
    if (keyboardEvent.shiftKey || keyboardEvent.isComposing) return;
    keyboardEvent.preventDefault();
    void this.saveEdit();
  }

  onAddComposerEnter(event: Event): void {
    const keyboardEvent = event as KeyboardEvent;
    if (keyboardEvent.shiftKey || keyboardEvent.isComposing) return;
    keyboardEvent.preventDefault();
    void this.saveAdd();
  }
}
